/* eslint-disable no-param-reassign  */
import React from 'react';
import range from 'lodash/range';
import * as Constants from './Constants';

import { getClosestTimeSteps } from '../../TimingHandler/TimingUtils';

/**
 * The Layercreator helps to initialize and update DataLayers which are time connected.
 * You need to pass a createLayer function to the constructor. We use that function to
 * create the actual tiles. A createLayerFunction looks like this example:
 *
 * function createTileLayer(props) {
 *   return (<LeafletTileLayer
 *     url={props.url}
 *     key={`TileLayer_${key}`
 *   />);
 * }
 */
class LayerCreator {
  constructor(createLayerFunction, urlKey) {
    this.createLayer = createLayerFunction;
    this.urlKey = urlKey;
  }

  /**
   * Initializes an array with DataLayers. Uses the passed createLayer function.
   * @param  {[type]} props [description]
   * @return {[type]}       [description]
   */
  initLayers(props) {
    const { config, currentTimestamp, beforeCount, afterCount } = props;

    const timeSteps = getClosestTimeSteps(
      config.runs[0].timesteps, currentTimestamp, beforeCount, afterCount
    );

    // we need to ensure that our this.tileLayers array has the correct size
    if (timeSteps.beforeTimesteps.length < beforeCount) {
      timeSteps.beforeTimesteps = range(beforeCount).map(index => {
        if (timeSteps.beforeTimesteps[index]) {
          return timeSteps.beforeTimesteps[index];
        }
        return {};
      });
    }

    if (timeSteps.afterTimesteps.length < afterCount) {
      timeSteps.afterTimesteps = range(afterCount).map(index => {
        if (timeSteps.afterTimesteps[index]) {
          return timeSteps.afterTimesteps[index];
        }
        return {};
      });
    }

    // putting all together in the correct order
    const timeStepArray = [].concat(
      timeSteps.beforeTimesteps, timeSteps.nearestTimestep, timeSteps.afterTimesteps
    );

    return {
      layers: timeStepArray.map((ts, index) => this.createLayer({
        url: ts[this.urlKey] ? `${Constants.TILE_HOST}${ts[this.urlKey]}` : ' ',
        // the element in the center gets opacity=0.8, the others 0
        opacity: index === beforeCount ? 0.8 : 0
      })),
      currentIndex: timeSteps.nearestTimestep.index
    };
  }
  /**
   * Returns one Layer if the Layer is not timeConnected and multiple if it is.
   * @param  {Object} props Component properties
   * @return {Array} One or more TileLayers
   */
  updateLayers(layers, currIndex, props, isPlaying) {
    if (!props.timeConnected) {
      return layers;
    }

    const { config, currentTimestamp, beforeCount, afterCount } = props;

    const maxStepLength = config.runs[0].timesteps.length - 1;
    const timeSteps = getClosestTimeSteps(
      config.runs[0].timesteps, currentTimestamp, beforeCount, afterCount
    );
    const nextActiveIndex = timeSteps.nearestTimestep.index;

    // only update if the next index is different from the current one
    if (nextActiveIndex === currIndex) {
      return currIndex;
    }

    // when user slides manually we need to reinit the layers
    const isBigStep = Math.abs(nextActiveIndex - currIndex) > 1;

    if (isBigStep) {
      const layersStuff = this.initLayers(props);

      layersStuff.layers.forEach((layer, i) => {
        layers[i] = layer;
      });

      return nextActiveIndex;
    }

    // forward mode means, that user is moving the slider from left to right
    const forwardMode = nextActiveIndex > currIndex || (currIndex === maxStepLength && isPlaying);

    // beforecount is our center/active index, thats why we add or subtract 1
    const nextIndexToActivate = forwardMode ? beforeCount + 1 : beforeCount - 1;
    // nextIndexToAdd is the index where we have to add the next element.
    // When we are in forwardmode we have to add it to the end,
    // when we are in backwardmode we have to add it to the front of the current layers.
    const nextIndexToAdd = forwardMode ? layers.length : 0;

    // we check which hidden layer we have to add next
    const nextToAdd = forwardMode ?
      timeSteps.afterTimesteps[timeSteps.afterTimesteps.length - 1] :
      timeSteps.beforeTimesteps[0];

    // we set the current active element (in the center) to opacity:0
    layers[beforeCount] = React.cloneElement(
      layers[beforeCount], { opacity: 0 }
    );

    // we set the next active element (before or after the last active one) to opacity:.8
    layers[nextIndexToActivate] = React.cloneElement(
      layers[nextIndexToActivate], { opacity: 0.8 }
    );

    const updateLayer = forwardMode ? layers.shift() : layers.pop();

    const nextUrlToUpdate = `${Constants.TILE_HOST}${nextToAdd[this.urlKey]}`;

    // we change the position of one of the hidden layers.
    // If we are in forwardMode, we change the pos from back to front and vice versa.
    layers.splice(nextIndexToAdd, 0, React.cloneElement(
      updateLayer, {
        url: nextUrlToUpdate,
        opacity: 0
      }
    ));

    return nextActiveIndex;
  }

}

export default LayerCreator;



/** WEBPACK FOOTER **
 ** ./core_components/DataLayer/Utils/LayerCreator.js
 **/